import { Decimal, isApiError, isZeroOrBlank } from "@compliance/domain";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCalendar,
  IconCalendarMonth,
  IconCalendarStats,
  IconCalendarTime,
  IconClipboardCheck,
  IconCoin,
  IconDownload,
  IconFileText,
  IconGasStation,
  IconHash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useArtifactSignedUrl,
  useBatch,
  useBatchFigures,
  useBatches,
  useCalibrationDue,
  useGenerateRfs0902Report,
  useGenerateRfs4800Report,
  useQuarterReports,
  useYearReports,
} from "../api/hooks";
import type { BatchState, BatchSummary, CalibrationDueItem, Figure } from "../api/types";
import { useHasRole } from "../auth";
import { BatchStateBadge } from "../components/BatchStateBadge";
import { UnitValue } from "../components/UnitValue";
import { LcfsQuarterModal } from "../components/lcfs/LcfsQuarterModal";
import { pickActiveBatch } from "../lib/batchState";
import { periodLabelFrom } from "../lib/datetime";
import { downloadUrl } from "../lib/download";
import { notifyDownloadBlocked } from "../lib/downloadNotify";
import { formatApiError } from "../lib/errors";
import {
  type QuarterGroup,
  type YearGroup,
  groupBatchesByQuarter,
  groupBatchesByYear,
} from "../lib/quarters";
import { queryKeys } from "../lib/queryKeys";
import { CreateBatchButton } from "./CreateBatchModal";

/**
 * A quarter report's kind. The live API names it by short name
 * (`Rfs4800QuarterlyReport`); the offline mocks by report_type
 * (`rfs4800_quarterly_report`). Either identifies it.
 */
function isReport(r: { short_name: string; report_type: string }, names: readonly string[]) {
  return names.includes(r.short_name) || names.includes(r.report_type);
}

const RFS4800_NAMES = ["Rfs4800QuarterlyReport", "rfs4800_quarterly_report"] as const;

/**
 * The quarter's LCFS CARB filing: fuel transactions + total throughput, and the
 * support package behind them — every utility statement and transaction
 * confirmation on file for the quarter, with the upload log and each month's
 * coverage verdict.
 */
const LCFS_QUARTER_FILES = [
  {
    names: ["LcfsQuarterlyFuelTransactions"],
    label: "LCFS QFTR",
    filename: (year: number, quarter: number) => `LCFS_QFTR_${year}_Q${quarter}.xlsx`,
  },
  {
    names: ["LcfsTotalThroughput"],
    label: "Total throughput",
    filename: (year: number, quarter: number) => `LCFS_Total_Throughput_${year}_Q${quarter}.xlsx`,
  },
  {
    names: ["LcfsSupportPackage"],
    label: "Support package",
    filename: (year: number, quarter: number) => `LCFS_Support_Package_${year}_Q${quarter}.zip`,
  },
] as const;

const STATE_RAIL: Record<BatchState, string> = {
  Open: "var(--mantine-color-gray-4)",
  PendingApproval: "var(--mantine-color-yellow-5)",
  Approved: "var(--mantine-color-teal-5)",
};

const NUMERIC_FONT: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
};

export function BatchesPage() {
  const batchesQ = useBatches();
  const activeBatchId = batchesQ.data
    ? (pickActiveBatch(batchesQ.data.items)?.batch_id ?? null)
    : null;
  const activeQ = useBatch(activeBatchId ?? undefined);

  // Active tab lives in the URL (state of record): ?view=quarterly|yearly;
  // monthly is the default and clears the param.
  const [params, setParams] = useSearchParams();
  const rawView = params.get("view");
  const view = rawView === "quarterly" || rawView === "yearly" ? rawView : "monthly";
  const setView = (next: string | null) => {
    const p = new URLSearchParams(params);
    if (next && next !== "monthly") p.set("view", next);
    else p.delete("view");
    setParams(p, { replace: true });
  };

  if (batchesQ.isPending) {
    return <BatchesPageSkeleton />;
  }
  if (batchesQ.isError) {
    return <ErrorState error={batchesQ.error} onRetry={() => batchesQ.refetch()} />;
  }

  const batches = batchesQ.data.items;
  const active = activeQ.data ?? null;

  return (
    <Stack gap="xl">
      <PageHeader />
      <CalibrationDueWidget />
      {active ? <SummaryStrip batch={active} /> : null}
      <Tabs value={view} onChange={setView} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab
            value="monthly"
            data-tour="batches-tab-monthly"
            leftSection={<IconCalendar size={14} />}
          >
            Monthly
          </Tabs.Tab>
          <Tabs.Tab
            value="quarterly"
            data-tour="batches-tab-quarterly"
            leftSection={<IconCalendarStats size={14} />}
          >
            Quarterly
          </Tabs.Tab>
          <Tabs.Tab
            value="yearly"
            data-tour="batches-tab-yearly"
            leftSection={<IconCalendarMonth size={14} />}
          >
            Yearly
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="monthly" pt="md">
          <Stack gap="md">
            <Group justify="space-between" align="baseline">
              <Title order={4}>All batches</Title>
            </Group>
            <Stack gap="sm">
              {batches.map((b) => (
                <BatchCard key={b.batch_id} batch={b} active={b.batch_id === activeBatchId} />
              ))}
            </Stack>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="quarterly" pt="md">
          <QuarterlyView batches={batches} activeBatchId={activeBatchId} />
        </Tabs.Panel>

        <Tabs.Panel value="yearly" pt="md">
          <YearlyView batches={batches} activeBatchId={activeBatchId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function BatchesPageSkeleton() {
  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Skeleton height={12} width={180} />
        <Skeleton height={32} width={280} mt={4} />
        <Skeleton height={14} width={420} mt={6} />
      </Stack>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <Skeleton height={92} radius="md" />
        <Skeleton height={92} radius="md" />
        <Skeleton height={92} radius="md" />
        <Skeleton height={92} radius="md" />
      </SimpleGrid>
      <Stack gap="sm">
        <Skeleton height={160} radius="md" />
        <Skeleton height={160} radius="md" />
        <Skeleton height={160} radius="md" />
      </Stack>
    </Stack>
  );
}

function ErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <Alert
      icon={<IconAlertCircle size={16} />}
      color="red"
      variant="light"
      title="Couldn't load batches"
      withCloseButton={false}
    >
      <Stack gap="xs">
        <Text size="sm">{error.message}</Text>
        <Text
          size="xs"
          c="indigo.7"
          fw={500}
          style={{ cursor: "pointer", width: "fit-content" }}
          onClick={onRetry}
        >
          Retry
        </Text>
      </Stack>
    </Alert>
  );
}

function PageHeader() {
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
      <Title order={1}>Reporting periods</Title>
      <CreateBatchButton />
    </Group>
  );
}

function SummaryStrip({ batch }: { batch: BatchSummary }) {
  const { tokenMmbtu, d3Rins, eligibleMmbtu, totalHubMscf } = useBatchFigures(batch);

  // If we have no figures at all (calc engine hasn't run for an Open
  // batch yet), skip the strip entirely rather than showing four "—"s.
  if (!tokenMmbtu && !d3Rins && !eligibleMmbtu && !totalHubMscf) return null;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
      {tokenMmbtu ? (
        <KpiTile
          icon={<IconGasStation size={18} />}
          color="indigo"
          label="Token MMBtu HHV"
          figure={tokenMmbtu}
        />
      ) : null}
      {d3Rins ? (
        <KpiTile icon={<IconCoin size={18} />} color="teal" label="D3 RINs" figure={d3Rins} />
      ) : null}
      {eligibleMmbtu ? (
        <KpiTile
          icon={<IconClipboardCheck size={18} />}
          color="grape"
          label="Eligible MMBtu"
          figure={eligibleMmbtu}
        />
      ) : null}
      {totalHubMscf ? (
        <KpiTile
          icon={<IconHash size={18} />}
          color="orange"
          label="Total HUB MSCF"
          figure={totalHubMscf}
        />
      ) : null}
    </SimpleGrid>
  );
}

function KpiTile({
  icon,
  color,
  label,
  figure,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  figure: Figure;
}) {
  return (
    <Paper p="lg" radius="md" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={8}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts="0.06em">
            {label}
          </Text>
          <Box style={NUMERIC_FONT}>
            <UnitValue figure={figure as never} showBasis={false} decimalPlaces={2} />
          </Box>
        </Stack>
        <ThemeIcon variant="light" color={color} radius="md" size={36}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

function BatchCard({ batch, active = false }: { batch: BatchSummary; active?: boolean }) {
  const { totalHubMscf, tokenMmbtu, utilityMmbtu, eligibleMmbtu, rfGallons, d3Rins } =
    useBatchFigures(batch);

  // "Empty" means we have no figures to show at all — every headline figure is
  // missing or zero. For an Open batch where the calc engine hasn't run, this
  // shows the "figures populate as intervals stream in" copy. We must NOT gate
  // solely on total_hub_scf: a created batch can compute the downstream figures
  // yet be missing the hub-SCF row, and blanking the whole card then hides real
  // data (mirrors Workpaper.tsx's all-figures gate).
  const allFigures = [totalHubMscf, tokenMmbtu, utilityMmbtu, eligibleMmbtu, rfGallons, d3Rins];
  const empty = allFigures.every((f) => !f || isZeroOrBlank(f.value));

  const periodLabel = batch.period_label ?? periodLabelFrom(batch.period_start);
  return (
    <Card
      data-tour={`batch-card-${batch.batch_id}`}
      component={Link}
      to={`/batches/${batch.batch_id}`}
      padding={0}
      radius="md"
      withBorder
      style={(t) => ({
        textDecoration: "none",
        color: "inherit",
        borderColor: active ? t.colors.indigo[3] : undefined,
        backgroundColor: active ? t.colors.indigo[0] : undefined,
        transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
      })}
      className="batch-card"
    >
      {/* Left state-colored rail */}
      <Box style={{ display: "flex", alignItems: "stretch" }}>
        <Box
          aria-hidden
          style={{
            width: 4,
            backgroundColor: STATE_RAIL[batch.state],
            borderTopLeftRadius: 6,
            borderBottomLeftRadius: 6,
            flexShrink: 0,
          }}
        />
        <Box style={{ flex: 1, padding: "20px 24px" }}>
          {/* Header — period + state on left, dates on right */}
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={6} style={{ minWidth: 0 }}>
              <Group gap="sm" wrap="wrap">
                <Title order={3} style={{ lineHeight: 1.1 }}>
                  {periodLabel}
                </Title>
                <BatchStateBadge state={batch.state} />
                {active ? (
                  <Badge variant="filled" color="indigo" size="sm" radius="sm">
                    Active period
                  </Badge>
                ) : null}
              </Group>
              <Group gap={6} wrap="wrap">
                <Text size="xs" ff="monospace" c="dimmed">
                  {batch.batch_id}
                </Text>
              </Group>
            </Stack>

            <Stack gap={2} align="flex-end">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
                Period
              </Text>
              <Text size="xs" ff="monospace" c="dimmed">
                {batch.period_start.slice(0, 10)} → {batch.period_end.slice(0, 10)}
              </Text>
            </Stack>
          </Group>

          {/* Figures grid or empty state */}
          <Box mt="lg">
            {empty ? (
              <Group gap="sm" py="sm">
                <ThemeIcon variant="light" color="gray" radius="xl" size={28}>
                  <IconHash size={14} />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Batch is open — figures populate as intervals stream in.
                </Text>
              </Group>
            ) : (
              <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="lg">
                {totalHubMscf ? (
                  <BigFigure label="Total HUB" unit="MSCF" figure={totalHubMscf} />
                ) : null}
                {tokenMmbtu ? (
                  <BigFigure label="Token" unit="MMBtu HHV" figure={tokenMmbtu} />
                ) : null}
                {utilityMmbtu ? (
                  <BigFigure label="Utility" unit="MMBtu HHV" figure={utilityMmbtu} />
                ) : null}
                {eligibleMmbtu ? (
                  <BigFigure label="Eligible" unit="MMBtu HHV" figure={eligibleMmbtu} />
                ) : null}
                {rfGallons ? (
                  <BigFigure label="RF Gallons" unit="gal" figure={rfGallons} emphasised />
                ) : null}
                {d3Rins ? (
                  <BigFigure label="D3 RINs" unit="rin" figure={d3Rins} emphasised />
                ) : null}
              </SimpleGrid>
            )}
          </Box>

          <Divider mt="lg" mb="md" />

          {/* Footer — CTA */}
          <Group justify="flex-end" align="center" wrap="wrap" gap="sm">
            <Group gap={6} wrap="nowrap">
              <Text size="sm" c="indigo.7" fw={500}>
                Open batch
              </Text>
              <IconArrowRight size={14} style={{ color: "var(--mantine-color-indigo-7)" }} />
            </Group>
          </Group>
        </Box>
      </Box>
    </Card>
  );
}

function BigFigure({
  label,
  unit,
  figure,
  emphasised = false,
}: {
  label: string;
  unit: string;
  figure: Figure;
  emphasised?: boolean;
}) {
  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Text
        size="xs"
        c="dimmed"
        tt="uppercase"
        fw={600}
        lts="0.06em"
        style={{ whiteSpace: "nowrap" }}
      >
        {label}
      </Text>
      <Text
        ff="monospace"
        fw={emphasised ? 600 : 500}
        size={emphasised ? "lg" : "md"}
        c={emphasised ? "indigo.8" : undefined}
        style={NUMERIC_FONT}
      >
        {formatForCard(figure.value)}
      </Text>
      <Text size="xs" c="dimmed">
        {unit}
      </Text>
    </Stack>
  );
}

// ── Quarterly view ──────────────────────────────────────────────────────────

function YearlyView({
  batches,
  activeBatchId,
}: {
  batches: BatchSummary[];
  activeBatchId: string | null;
}) {
  // RFS0902 (annual) generation is gated like the other report actions.
  const canGenerate = useHasRole("reviewer", "admin");
  const groups = groupBatchesByYear(batches);

  if (groups.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No batches to group into years yet.
      </Text>
    );
  }

  return (
    <Stack gap="xl">
      {groups.map((group) => (
        <YearSection
          key={group.key}
          group={group}
          activeBatchId={activeBatchId}
          canGenerate={canGenerate}
        />
      ))}
    </Stack>
  );
}

function YearSection({
  group,
  activeBatchId,
  canGenerate,
}: {
  group: YearGroup;
  activeBatchId: string | null;
  canGenerate: boolean;
}) {
  const generate = useGenerateRfs0902Report();
  const signedUrl = useArtifactSignedUrl();
  const reportsQ = useYearReports(group.year);
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);

  // One RFS0902 per year (recorded under the year's closing-month batch).
  const existing = reportsQ.data?.[0] ?? null;

  const onGenerate = async () => {
    try {
      await generate.mutateAsync({
        year: group.year,
        batchIds: group.batches.map((b) => b.batch_id),
      });
      notifications.show({
        title: "RFS0902 generated",
        message: `${group.year} annual report generated — download from this year.`,
        color: "teal",
        autoClose: 4000,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.years.reports(group.year),
      });
    } catch (err) {
      notifications.show({
        title: "RFS0902 generation failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const onDownload = async () => {
    if (!existing) return;
    setDownloading(true);
    try {
      const result = await signedUrl.mutateAsync({
        kind: "report",
        id: existing.report_id,
        reason: `Download RFS0902 for ${group.year}`,
      });
      downloadUrl(result.url, `RFS0902_${group.year}.xlsx`, {
        onBlocked: () => notifyDownloadBlocked("RFS0902"),
      });
    } catch (err) {
      if (isApiError(err) && err.httpStatus === 404) {
        notifications.show({
          title: "Report not yet on the backend",
          message:
            "The artifact endpoint is live, but this report_id only exists locally (MSW). Generate against the live backend before downloading.",
          color: "yellow",
          autoClose: 7000,
        });
      } else {
        notifications.show({
          title: "Download failed",
          message: formatApiError(err),
          color: "red",
          autoClose: 5000,
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Group gap="sm" align="baseline">
          <Title order={4}>{group.year}</Title>
          <Text size="xs" c="dimmed">
            {group.batches.length} month{group.batches.length === 1 ? "" : "s"}
          </Text>
        </Group>
        {canGenerate ? (
          <Group gap="xs">
            {existing ? (
              <Button
                size="xs"
                variant="default"
                leftSection={<IconDownload size={14} />}
                loading={downloading}
                onClick={onDownload}
              >
                Download RFS0902
              </Button>
            ) : null}
            <Button
              size="xs"
              variant="light"
              leftSection={<IconFileText size={14} />}
              loading={generate.isPending}
              onClick={onGenerate}
              data-tour="year-generate"
            >
              {existing ? "Regenerate RFS0902" : "RFS0902 reports"}
            </Button>
          </Group>
        ) : null}
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {group.batches.map((b) => (
          <CompactBatchCard key={b.batch_id} batch={b} active={b.batch_id === activeBatchId} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

function QuarterlyView({
  batches,
  activeBatchId,
}: {
  batches: BatchSummary[];
  activeBatchId: string | null;
}) {
  // RFS4800 report generation is gated like the other report actions.
  const canGenerate = useHasRole("reviewer", "admin");
  const groups = groupBatchesByQuarter(batches);

  if (groups.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No batches to group into quarters yet.
      </Text>
    );
  }

  return (
    <Stack gap="xl">
      {groups.map((group) => (
        <QuarterSection
          key={group.key}
          group={group}
          activeBatchId={activeBatchId}
          canGenerate={canGenerate}
        />
      ))}
    </Stack>
  );
}

function QuarterSection({
  group,
  activeBatchId,
  canGenerate,
}: {
  group: QuarterGroup;
  activeBatchId: string | null;
  canGenerate: boolean;
}) {
  const generate = useGenerateRfs4800Report();
  const signedUrl = useArtifactSignedUrl();
  const reportsQ = useQuarterReports(group.year, group.quarter);
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [lcfsOpen, setLcfsOpen] = useState(false);
  const [lcfsDownloading, setLcfsDownloading] = useState<string | null>(null);

  // One RFS4800 per quarter (recorded under the closing-month batch), listed
  // beside the LCFS set — so pick it by name, never by position.
  const reports = reportsQ.data ?? [];
  const existing = reports.find((r) => isReport(r, RFS4800_NAMES)) ?? null;
  const lcfsReports = LCFS_QUARTER_FILES.map((f) => ({
    ...f,
    report: reports.find((r) => isReport(r, f.names)) ?? null,
  })).filter((f) => f.report != null);

  const first = group.batches[0];
  const last = group.batches[group.batches.length - 1];
  const range =
    first && last
      ? group.batches.length > 1
        ? `${periodLabelFrom(first.period_start)} – ${periodLabelFrom(last.period_start)}`
        : periodLabelFrom(first.period_start)
      : "";

  const onGenerate = async () => {
    try {
      await generate.mutateAsync({
        year: group.year,
        quarter: group.quarter,
        batchIds: group.batches.map((b) => b.batch_id),
      });
      notifications.show({
        title: "RFS4800 generated",
        message: `${group.label} quarterly report generated — download from this quarter.`,
        color: "teal",
        autoClose: 4000,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.quarters.reports(group.year, group.quarter),
      });
    } catch (err) {
      notifications.show({
        title: "RFS4800 generation failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const onDownload = async () => {
    if (!existing) return;
    setDownloading(true);
    try {
      const result = await signedUrl.mutateAsync({
        kind: "report",
        id: existing.report_id,
        reason: `Download RFS4800 for ${group.label}`,
      });
      downloadUrl(result.url, `RFS4800_${group.year}_Q${group.quarter}.xlsx`, {
        onBlocked: () => notifyDownloadBlocked("RFS4800"),
      });
    } catch (err) {
      if (isApiError(err) && err.httpStatus === 404) {
        notifications.show({
          title: "Report not yet on the backend",
          message:
            "The artifact endpoint is live, but this report_id only exists locally (MSW). Generate against the live backend before downloading.",
          color: "yellow",
          autoClose: 7000,
        });
      } else {
        notifications.show({
          title: "Download failed",
          message: formatApiError(err),
          color: "red",
          autoClose: 5000,
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  const onDownloadLcfs = async (reportId: string, filename: string, what: string) => {
    setLcfsDownloading(reportId);
    try {
      const result = await signedUrl.mutateAsync({
        kind: "report",
        id: reportId,
        reason: `Download ${what} for ${group.label}`,
      });
      downloadUrl(result.url, filename, { onBlocked: () => notifyDownloadBlocked(what) });
    } catch (err) {
      notifications.show({
        title: "Download failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    } finally {
      setLcfsDownloading(null);
    }
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Group gap="sm" align="baseline">
          <Title order={4}>{group.label}</Title>
          <Text size="xs" c="dimmed">
            {range} · {group.batches.length} month{group.batches.length === 1 ? "" : "s"}
          </Text>
        </Group>
        {canGenerate ? (
          <Group gap="xs">
            {existing ? (
              <Button
                size="xs"
                variant="default"
                leftSection={<IconDownload size={14} />}
                loading={downloading}
                onClick={onDownload}
                data-tour="quarter-download"
              >
                Download RFS4800
              </Button>
            ) : null}
            <Button
              size="xs"
              variant="light"
              leftSection={<IconFileText size={14} />}
              loading={generate.isPending}
              onClick={onGenerate}
              data-tour="quarter-generate"
            >
              {existing ? "Regenerate RFS4800" : "RFS4800 reports"}
            </Button>
            {lcfsReports.map((f) =>
              f.report ? (
                <Button
                  key={f.label}
                  size="xs"
                  variant="default"
                  leftSection={<IconDownload size={14} />}
                  loading={lcfsDownloading === f.report.report_id}
                  data-tour={`quarter-lcfs-download-${f.label.replace(/\W+/g, "-").toLowerCase()}`}
                  onClick={() =>
                    f.report &&
                    onDownloadLcfs(
                      f.report.report_id,
                      f.filename(group.year, group.quarter),
                      f.label,
                    )
                  }
                >
                  Download {f.label}
                </Button>
              ) : null,
            )}
            <Button
              size="xs"
              variant="light"
              color="grape"
              leftSection={<IconGasStation size={14} />}
              onClick={() => setLcfsOpen(true)}
              data-tour="quarter-lcfs"
            >
              {lcfsReports.length > 0 ? "Regenerate LCFS" : "LCFS report"}
            </Button>
          </Group>
        ) : null}
      </Group>
      {canGenerate ? (
        <LcfsQuarterModal
          opened={lcfsOpen}
          onClose={() => setLcfsOpen(false)}
          year={group.year}
          quarter={group.quarter}
          label={group.label}
        />
      ) : null}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {group.batches.map((b) => (
          <CompactBatchCard key={b.batch_id} batch={b} active={b.batch_id === activeBatchId} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

function CompactBatchCard({ batch, active = false }: { batch: BatchSummary; active?: boolean }) {
  const { tokenMmbtu } = useBatchFigures(batch);
  const periodLabel = batch.period_label ?? periodLabelFrom(batch.period_start);
  return (
    <Card
      component={Link}
      to={`/batches/${batch.batch_id}`}
      padding="md"
      radius="md"
      withBorder
      style={(t) => ({
        textDecoration: "none",
        color: "inherit",
        borderColor: active ? t.colors.indigo[3] : undefined,
        backgroundColor: active ? t.colors.indigo[0] : undefined,
      })}
      className="batch-card"
    >
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="sm" lh={1.2}>
            {periodLabel}
          </Text>
          <BatchStateBadge state={batch.state} />
        </Group>
        <Text size="xs" ff="monospace" c="dimmed">
          {batch.batch_id}
        </Text>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
            Token MMBtu HHV
          </Text>
          {tokenMmbtu ? (
            <Box style={NUMERIC_FONT}>
              <UnitValue figure={tokenMmbtu as never} showBasis={false} decimalPlaces={2} />
            </Box>
          ) : (
            <Text size="sm" c="dimmed">
              Not calculated yet
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

function formatForCard(raw: string): string {
  try {
    const d = new Decimal(raw);
    if (d.isZero()) return "0";
    return d.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } catch {
    return "—";
  }
}

// ── Calibration-due widget (BE: GET /equipment/calibration-due) ────────────

/**
 * Dashboard widget summarising equipment with calibration overdue or
 * due in the next 30 days. Renders nothing while loading or when no
 * items are returned — we don't want a "Loading…" banner cluttering
 * the batches page header for a feature most of the time will be empty.
 */
function CalibrationDueWidget() {
  const cal = useCalibrationDue(30);
  const items = cal.data ?? [];
  if (items.length === 0) return null;

  // `missing` (no calibration record at all) counts with overdue: both mean the
  // instrument is not in valid calibration, and both block approval (BIO-309).
  const missing = items.filter((i) => i.status === "missing").length;
  const overdue = items.filter((i) => i.status === "overdue").length;
  const soon = items.length - overdue - missing;
  const blocking = overdue + missing;

  return (
    <Paper
      data-tour="calibration-due"
      withBorder
      radius="md"
      p="md"
      style={(t) => ({
        borderColor: blocking > 0 ? t.colors.red[3] : t.colors.yellow[3],
        backgroundColor: blocking > 0 ? t.colors.red[0] : t.colors.yellow[0],
      })}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color={blocking > 0 ? "red" : "yellow"} radius="md" size={36}>
          <IconCalendarTime size={20} />
        </ThemeIcon>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="sm" wrap="wrap">
            <Text fw={600} size="sm">
              Calibration attention needed
            </Text>
            {missing > 0 ? (
              <Badge color="red" size="sm" variant="filled">
                {missing} never calibrated
              </Badge>
            ) : null}
            {overdue > 0 ? (
              <Badge color="red" size="sm" variant="filled">
                {overdue} overdue
              </Badge>
            ) : null}
            {soon > 0 ? (
              <Badge color="yellow" size="sm" variant="light">
                {soon} due within 30d
              </Badge>
            ) : null}
          </Group>
          <Stack gap={2}>
            {items.slice(0, 4).map((item) => (
              <CalibrationDueRow key={item.tag} item={item} />
            ))}
            {items.length > 4 ? (
              <Text size="xs" c="dimmed">
                +{items.length - 4} more
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </Group>
    </Paper>
  );
}

/**
 * The countdown text for one calibration row. A `missing` item has no due date
 * (BIO-309), so it states the condition instead of counting down to a date the
 * BE deliberately sent as null.
 */
export function calibrationRowLabel(item: CalibrationDueItem): string {
  if (item.status === "missing" || item.days_until_due === null) {
    return "no calibration on record";
  }
  return item.status === "overdue"
    ? `${Math.abs(item.days_until_due)}d overdue`
    : `due in ${item.days_until_due}d`;
}

function CalibrationDueRow({ item }: { item: CalibrationDueItem }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="xs" ff="monospace" fw={500}>
        {item.tag}
      </Text>
      <Text size="xs" c="dimmed" truncate>
        {item.kind}
        {item.facility_identifier ? ` · ${item.facility_identifier}` : ""}
      </Text>
      <Text size="xs" c="dimmed">
        ·
      </Text>
      <Text size="xs" c={item.status === "due_soon" ? "yellow.8" : "red.7"} fw={500}>
        {calibrationRowLabel(item)}
      </Text>
    </Group>
  );
}
